import { TFunction } from 'i18next'

import { ContactVersionEnum } from '../../../../../apps/backoffice/src/api/enrollments/get-has-transport'
import { SidebarItemType } from '../../../../../apps/backoffice/src/components/elements/sidebar/sidebar-item'
import { SidebarItemKey } from '../../../../../apps/backoffice/src/components/layouts/main-layout'
import { Paths } from '../../../../../apps/backoffice/src/lib/constants/path'
import { BillingTypeEnum } from '../../../../../apps/backoffice/src/lib/types/enums/cse/billing-type.enum'
import { OLTypeEnum } from '../../../../../apps/backoffice/src/lib/types/enums/ol/ol-type.enum'
import { RoleEnum } from '../../../../../apps/backoffice/src/lib/types/enums/users/role.enum'
import { AffectedStaffView } from '../../../../../apps/backoffice/src/lib/types/models/affectedStaff/affected-staff'
import {
  ActivityIcon,
  BookmarkIcon,
  BuildingIcon,
  BuildingThreeIcon,
  BuildingTwoIcon,
  BusIcon,
  CalendarSevenIcon,
  FamilyIcon,
  FeesThreeIcon,
  FormIcon,
  HomeIcon,
  ListApprovedIcon,
  ProviderIcon,
  SettingIcon,
  SheetOutlineIcon,
  TeachingIcon,
  TelephoneIcon,
  TravelTwoIcon,
  UserFourIcon,
  UserSevenIcon,
  UserTwoIcon
} from '../../../../ui/icons'
import { AvailableLanguages } from '../../types/language.types'
import { DateFormatter } from '../date.utils'
import { constructAffectedStaffPath, constructClientPath } from './construct-path'

type NavbarOptions = {
  hideItems?: SidebarItemKey[]
}

type NavBarParams = {
  role: RoleEnum
  t: TFunction<AvailableLanguages, undefined>
  selectedEnrollmentId?: number
  options?: NavbarOptions
  affectedStaffsData?: AffectedStaffView[]
  enrollmentContactVersion?: ContactVersionEnum
  olType?: OLTypeEnum
  enrollmentHasTest?: boolean
  enrollmentCancelled?: boolean
  cseBillingType?: BillingTypeEnum
  /**
   * a One to One Individuel enrollment has no center, no journey and no trip, so the sidebar
   * entries that describe them never apply to it
   */
  enrollmentIsOtoIndividual?: boolean
}

export const getNavbarItems = ({
  t,
  role,
  olType,
  options,
  affectedStaffsData,
  selectedEnrollmentId,
  enrollmentContactVersion,
  enrollmentHasTest,
  enrollmentCancelled = true,
  cseBillingType,
  enrollmentIsOtoIndividual
}: NavBarParams): SidebarItemType[] => {
  const { hideItems } = options || {}

  const allItems: SidebarItemType[] = [
    {
      itemKey: 'system-admin-users-list-page',
      text: 'Utilisateurs système',
      hasAccess: [RoleEnum.SYSTEM_ADMIN].includes(role),
      icon: UserSevenIcon,
      url: Paths.SYSTEM_ADMIN_USERS_LIST_PAGE
    },
    {
      itemKey: 'staff-personal-space' as SidebarItemKey,
      text: t('staff.personal.space'),
      hasAccess: [RoleEnum.STAFF].includes(role),
      icon: UserFourIcon,
      items: [
        {
          itemKey: 'staff-personal-space-personal-info',
          text: t('staff.personal.space.personal.info'),
          url: Paths.STAFF_PERSONAL_SPACE_INFORMATION
        },
        {
          itemKey: 'staff-personal-space-contracts',
          text: t('staff.personal.space.contracts'),
          url: Paths.STAFF_PERSONAL_SPACE_CONTRACTS
        },
        {
          itemKey: 'staff-personal-space-missions',
          text: t('staff.personal.space.missions'),
          url: Paths.STAFF_PERSONAL_SPACE_MISSIONS
        },
        {
          itemKey: 'staff-personal-space-availability',
          text: t('staff.personal.space.availability'),
          url: Paths.STAFF_PERSONAL_SPACE_AVAILABILITY
        },
        {
          itemKey: 'staff-personal-space-availability-requests',
          text: t('staff.personal.space.pay.scale'),
          url: Paths.STAFF_PERSONAL_SPACE_PAY_SCALE_TABLE
        },
        {
          itemKey: 'staff-personal-space-my-journeys',
          text: t('staff.personal.space.my.journeys'),
          items: [
            {
              itemKey: 'staff-personal-space-my-journeys-home',
              text: t('staff.personal.space.my.journeys.home'),
              url: Paths.STAFF_PERSONAL_SPACE_MY_JOURNEYS,
              activeUrls: [Paths.STAFF_PERSONAL_SPACE_MY_JOURNEYS]
            },
            // Generate journey items from staffRequestData (if provided).
            // Each item will have a unique itemKey so it can be hidden/targeted,
            // and a URL pointing to the journey details page using the request id.
            ...(affectedStaffsData && affectedStaffsData.length
              ? affectedStaffsData.map((sr) => ({
                  // ensure a unique SidebarItemKey per affected staff journey
                  // include "details" to match the SidebarItemKey union pattern
                  itemKey:
                    `staff-personal-space-my-journeys-details-${sr.id}` as unknown as SidebarItemKey,
                  text: `${sr.staffRequest.country ? `${sr.staffRequest.country.name} - ` : ''}${DateFormatter.DDMMYYYY(sr.staffRequest.startDate)} - ${DateFormatter.DDMMYYYY(sr.staffRequest.endDate)}`,
                  url: constructAffectedStaffPath(
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_HOME,
                    sr.id
                  ),
                  // ensure this generated item is considered active when visiting its detail page
                  activeUrls: [
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_GUIDE,
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_TRIP_INFORMATION,
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_STUDENTS,
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_ACTIVITIES_AND_BUDGET,
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_QUESTIONNAIRE,
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_EXPENSES_NOTE,
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_DAILY_REPORT,
                    Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_END_OF_STAY_REPORT
                  ].map((path) => constructAffectedStaffPath(path, sr.id))
                }))
              : [])
          ]
        }
      ]
    },
    {
      itemKey: 'requests',
      text: 'Demandes',
      hasAccess: [RoleEnum.ADMIN].includes(role),
      icon: BookmarkIcon,
      items: [
        {
          itemKey: 'requests-brochures',
          text: 'Brochures',
          items: [
            {
              itemKey: 'information-request-create',
              text: 'Créer une demande de brochure',
              url: Paths.INFORMATION_REQUEST_CREATE_PAGE
            },
            {
              text: 'Demandes de brochures',
              itemKey: 'information-request-list',
              url: Paths.INFORMATION_REQUESTS_PAGE,
              activeUrls: [Paths.INFORMATION_REQUEST_UPDATE_PAGE]
            }
          ]
        },
        {
          itemKey: 'requests-quotations',
          text: 'Devis',
          items: [
            {
              text: 'Créer une demande de devis',
              itemKey: 'enrollment-quotation-create',
              url: Paths.ENROLLMENT_QUOTATION_CREATE_PAGE
            },
            {
              itemKey: 'enrollment-quotation-list',
              text: 'Demandes de devis',
              url: Paths.ENROLLMENT_QUOTATION_LIST_PAGE,
              activeUrls: [Paths.ENROLLMENT_QUOTATION_UPDATE_PAGE]
            }
          ]
        },
        {
          itemKey: 'requests-quotations-ol',
          text: 'Inscriptions',
          items: [
            {
              text: "Créer une demande d'inscription",
              itemKey: 'enrollment-create',
              url: Paths.ENROLLMENT_CREATE_PAGE
            },
            {
              text: "Demandes d'inscription à traiter",
              itemKey: 'enrollment-list',
              url: Paths.ENROLLMENT_LIST_PAGE,
              activeUrls: [Paths.ENROLLMENT_UPDATE_PAGE]
            }
          ]
        }
      ]
    },
    {
      itemKey: 'management-of-registrants',
      text: 'Gestion des inscrits',
      hasAccess: [RoleEnum.ADMIN].includes(role),
      icon: BookmarkIcon,
      items: [
        {
          text: 'Suivi des inscrits confirmés',
          url: Paths.ENROLLMENT_VALIDATED_LIST_PAGE,
          itemKey: 'enrollment-validated-list',
          activeUrls: [Paths.ENROLLMENT_PROFILE_PAGE]
        },
        {
          text: 'Liste des centres',
          url: Paths.CENTER_LIST_PAGE,
          itemKey: 'center-list',
          activeUrls: [Paths.CENTER_DETAILS_PAGE, Paths.ENROLLMENT_PROFILE_PAGE]
        },
        {
          text: 'Evénements en séjour',
          url: Paths.CENTER_EVENT_LIST_PAGE,
          itemKey: 'center-event-list',
          activeUrls: [Paths.CENTER_EVENT_DETAILS_PAGE]
        },
        {
          text: 'Suivi des paiements',
          url: Paths.CLIENT_PAYMENTS_LIST_PAGE,
          itemKey: 'client-payments-list-page'
        },
        {
          text: 'Appréciations de fin de séjour',
          url: Paths.SURVEY_END_OF_STAY_APPRECIATIONS_SUMMARY_PAGE,
          itemKey: 'end-of-stay-appreciations-summary-page'
        },
        {
          text: 'Traitement des questionnaires',
          url: Paths.SURVEY_END_OF_STAY_QUESTIONNAIRES_PROCESSING_PAGE,
          itemKey: 'end-of-stay-questionnaires-processing-page'
        }
      ]
    },
    {
      itemKey: 'providers',
      text: 'Etranger',
      hasAccess: [RoleEnum.ADMIN].includes(role),
      icon: ProviderIcon,
      items: [
        {
          itemKey: 'providers-center-list',
          text: 'Gestion des centres',
          items: [
            {
              text: 'Liste des centres',
              url: Paths.OL_CENTER_LIST_PAGE,
              itemKey: 'ol-center-list-page',
              activeUrls: [Paths.OL_CENTER_DETAILS_PAGE]
            },
            {
              text: 'Liste des OTO Indiv',
              url: Paths.OTO_INDIVIDUAL_PLACE_PERIODS_LIST_PAGE,
              itemKey: 'oto-individual-place-list',
              activeUrls: [Paths.OTO_INDIVIDUAL_PLACES_BY_PERIOD_LIST_PAGE]
            },
            {
              text: 'Enveloppe centre',
              url: Paths.RAD_LIST_PAGE,
              itemKey: 'ol-rad-list-page',
              activeUrls: [Paths.RAD_UPDATE_PAGE, Paths.RAD_CREATE_PAGE]
            }
          ]
        },
        {
          itemKey: 'providers-parameters',
          text: 'Gestion des paiements',
          items: [
            {
              text: 'Prestas LEC/suivi & paiements',
              itemKey: 'center-payments',
              url: Paths.CENTER_PAYMENTS_LIST_PAGE
            },
            {
              text: 'Virements',
              itemKey: 'center-ol-payments',
              url: Paths.CENTER_OL_PAYMENTS_LIST_PAGE,
              activeUrls: [
                Paths.CENTER_OL_PAYMENTS_CREATE_PAGE,
                Paths.CENTER_OL_PAYMENTS_UPDATE_PAGE
              ]
            },
            {
              text: 'Fees Prestas LEC',
              url: Paths.OL_COMMISSIONS_LIST_PAGE,
              itemKey: 'ol-commissions-list-page',
              activeUrls: [Paths.OL_COMMISSION_UPDATE_PAGE, Paths.OL_COMMISSION_CREATE_PAGE]
            },
            {
              text: 'Comptes bancaires LEC',
              itemKey: 'lec-bank-accounts',
              url: Paths.LEC_BANK_ACCOUNTS_LIST_PAGE,
              activeUrls: [
                Paths.LEC_BANK_ACCOUNTS_CREATE_PAGE,
                Paths.LEC_BANK_ACCOUNTS_UPDATE_PAGE
              ]
            }
          ]
        },
        {
          itemKey: 'providers-ol-list',
          text: 'Gestion des prestataires',
          items: [
            {
              text: 'Prestataires étrangers',
              url: Paths.OL_LIST_PAGE,
              itemKey: 'ol-list-page',
              activeUrls: [
                Paths.OL_UPDATE_PAGE,
                Paths.OL_DETAILS_PAGE,
                Paths.OL_CREATE_PAGE,
                Paths.OL_HOST_FAMILY_INFORMATION
              ]
            },
            {
              text: 'Familles-hôtesses',
              url: Paths.OL_HOST_FAMILY_GLOBAL_LIST_PAGE,
              itemKey: 'ol-host-family-global-list-page',
              activeUrls: [Paths.OL_HOST_FAMILY_INFORMATION]
            },
            {
              text: 'TEFL',
              url: Paths.OL_TEFL_GLOBAL_LIST_PAGE,
              itemKey: 'ol-tefl-global-list-page',
              activeUrls: [Paths.OL_TEFL_DETAILS_PAGE]
            },
            {
              text: 'Teaching halls',
              url: Paths.OL_TEACHING_HALL_GLOBAL_LIST_PAGE,
              itemKey: 'ol-teaching-hall-global-list-page',
              activeUrls: [Paths.OL_TEACHING_HALL_VIEW_PAGE]
            },
            {
              text: 'Coach companies',
              url: Paths.OL_COACH_COMPANIES_GLOBAL_LIST_PAGE,
              itemKey: 'ol-coach-companies-global-list-page',
              activeUrls: [Paths.OL_COACH_COMPANIES_VIEW_PAGE]
            }
          ]
        }
      ]
    },

    {
      itemKey: 'trips',
      text: 'Voyages',
      hasAccess: [RoleEnum.ADMIN].includes(role),
      icon: TravelTwoIcon,
      items: [
        {
          text: 'Gestion des réservations',
          itemKey: 'trips-trip-booking',
          items: [
            {
              text: 'Réservations voyages',
              url: Paths.TRIP_BOOKING_LIST_PAGE,
              itemKey: 'trip-booking-list-page',
              activeUrls: [Paths.TRIP_BOOKING_UPDATE_PAGE, Paths.TRIP_BOOKING_CREATE_PAGE]
            },
            {
              text: 'Places à répartir',
              itemKey: 'trip-dates-list-page',
              url: Paths.TRIP_DATES_LIST_PAGE
            },
            {
              text: 'Transferts',
              url: Paths.TRANSFER_LIST_PAGE,
              itemKey: 'transfer-list-page',
              activeUrls: [Paths.TRANSFER_UPDATE_PAGE, Paths.TRANSFER_CREATE_PAGE]
            }
          ]
        },
        {
          text: 'Gestion des AAT',
          itemKey: 'trips-aat',
          items: [
            {
              text: 'Créer une demande de mission AAT',
              url: Paths.TRIP_STEPS_LIST_PAGE,
              itemKey: 'trip-steps-list-page'
            },
            {
              text: 'Liste des missions AAT',
              url: Paths.MISSION_LIST_PAGE,
              itemKey: 'mission-list-page',
              activeUrls: [Paths.MISSION_VIEW_PAGE]
            }
          ]
        },
        {
          text: 'Logistique terrain',
          itemKey: 'trips-logistics',
          items: [
            {
              text: 'Voyages principaux réservés',
              url: Paths.PRIMARY_BOOKED_TRIP_DATES_LIST_PAGE,
              itemKey: 'primary-booked-trip-dates-list-page',
              activeUrls: [Paths.TRIP_DATES_UPDATE_PAGE]
            },
            {
              text: 'Voyages secondaires réservés',
              itemKey: 'secondary-booked-trip-dates-list-page',
              url: Paths.SECONDARY_BOOKED_TRIP_DATES_LIST_PAGE,
              activeUrls: [Paths.TRIP_DATES_UPDATE_PAGE]
            },
            {
              text: 'Liste des dossiers',
              itemKey: 'trip-folder-list-page',
              url: Paths.TRIP_FOLDER_LIST_PAGE,
              activeUrls: [Paths.TRIP_FOLDER_DETAILS_PAGE, Paths.TRIP_FOLDER_DETAILS_PAGE]
            },
            {
              text: 'Timeline des jours de voyage',
              itemKey: 'trips-monitoring',
              url: Paths.TRIPS_MONITORING
            }
          ]
        },
        {
          text: 'Paramètres',
          itemKey: 'trips-parameters',
          items: [
            {
              text: 'Prestataires',
              url: Paths.TRIP_PROVIDER_LIST_PAGE,
              itemKey: 'trip-provider-list-page',
              activeUrls: [Paths.TRIP_PROVIDER_UPDATE_PAGE, Paths.TRIP_PROVIDER_CREATE_PAGE]
            },
            {
              text: 'Compagnies de voyages',
              url: Paths.SETTING_TRAVEL_COMPANIES,
              itemKey: 'setting-travel-companies'
            },

            {
              text: 'Consignes aux compagnies',
              url: Paths.TRIP_BOOKING_GUIDELINE_LIST_PAGE,
              itemKey: 'trip-booking-guideline-list-page',
              activeUrls: [
                Paths.TRIP_BOOKING_GUIDELINE_UPDATE_PAGE,
                Paths.TRIP_BOOKING_GUIDELINE_CREATE_PAGE
              ]
            },
            {
              text: 'Adresses des RDV voyages',
              url: Paths.TRIP_ADDRESS_LIST_PAGE,
              itemKey: 'trip-address-list-page',
              activeUrls: [Paths.TRIP_ADDRESS_UPDATE_PAGE, Paths.TRIP_ADDRESS_CREATE_PAGE]
            }
          ]
        }
      ]
    },

    {
      itemKey: 'supervisors',
      text: 'Encadrants',
      hasAccess: [RoleEnum.ADMIN].includes(role),
      icon: UserSevenIcon,
      items: [
        {
          text: 'Gestion des candidatures',
          itemKey: 'staff-applications',
          items: [
            {
              text: 'Candidatures',
              url: Paths.STAFF_APPLICATIONS_PAGE,
              itemKey: 'staff-applications-list-page',
              activeUrls: [Paths.UPDATE_STAFF_APPLICATION_PAGE]
            },
            {
              text: 'Suivi des PCR',
              url: Paths.PCRS_PAGE,
              itemKey: 'pcrs-list-page',
              activeUrls: [Paths.PCR_UPDATE_PAGE, Paths.PCR_CREATE_PAGE, Paths.PCR_KANBAN_BOARD]
            }
          ]
        },
        {
          text: 'Gestion des encadrants',
          itemKey: 'staff-management',
          items: [
            {
              text: 'Encadrants',
              url: Paths.STAFF_LIST_PAGE,
              itemKey: 'staff-list-page',
              activeUrls: [Paths.STAFF_UPDATE_PAGE, Paths.STAFF_CREATE_PAGE]
            },
            {
              text: 'Suivi administratif des encadrants',
              url: Paths.AFFECTED_STAFFS_LIST_PAGE,
              itemKey: 'staff-monitoring-list-page',
              activeUrls: [Paths.AFFECTED_STAFFS_VIEW_PAGE]
            },
            {
              text: 'Suivi administratif des AAT',
              url: Paths.MISSION_TRACKING_LIST_PAGE,
              itemKey: 'mission-tracking-list-page',
              activeUrls: [Paths.MISSION_TRACKING_UPDATE_PAGE]
            },
            {
              text: 'Documents à valider',
              url: Paths.STAFF_DOCUMENT_LIST_PAGE,
              itemKey: 'staff-document-list-page'
            }
          ]
        },
        {
          text: 'Gestion des séjours et missions',
          itemKey: 'staff-trips',
          items: [
            {
              text: "Demandes d'encadrants - Séjours",
              url: Paths.STAFF_REQUEST_LIST_PAGE,
              itemKey: 'staff-request-list-page',
              activeUrls: [Paths.STAFF_REQUEST_DETAIL_PAGE]
            },
            {
              text: "Demandes d'AAT -  Missions",
              url: Paths.MISSION_LIST_STAFF_PAGE,
              itemKey: 'mission-list-staff',
              activeUrls: [Paths.MISSION_VIEW_PAGE]
            },
            {
              text: 'Réunions avant départ',
              url: Paths.RAD_LIST_PAGE,
              itemKey: 'rad-list-page',
              activeUrls: [Paths.RAD_UPDATE_PAGE, Paths.RAD_CREATE_PAGE]
            },
            {
              text: 'Budgets séjours',
              url: Paths.BUDGET_ALLOCATION_LIST_PAGE,
              itemKey: 'allocation-budget-list-page',
              activeUrls: [Paths.BUDGET_ALLOCATION_CREATE_PAGE]
            },
            {
              text: 'Gestion des notes de frais',
              url: Paths.STAFF_EXPENSE_REPORT_LIST_PAGE,
              itemKey: 'staff-expense-notes-list-page',
              activeUrls: [
                Paths.AFFECTED_STAFF_EXPENSE_REPORT_DETAILS_PAGE,
                Paths.MISSION_TO_STAFF_EXPENSE_REPORT_DETAILS_PAGE
              ]
            },
            {
              text: 'Diffusions infos aux ME',
              url: Paths.AFFECTED_STAFF_DIFFUSIONS_LIST_PAGE,
              itemKey: 'affected-staff-diffusions-list-page'
            }
          ]
        },
        {
          text: 'Gestion des paiements encadrants',
          itemKey: 'staff-payments',
          items: [
            {
              text: 'Honoraires encadrants',
              url: Paths.AFFECTED_STAFFS_SALARIES_LIST_PAGE,
              itemKey: 'affected-staffs-salaries-list-page'
            },
            {
              text: 'Honoraires AAT',
              url: Paths.MISSION_TRACKINGS_SALARIES_LIST_PAGE,
              itemKey: 'mission-trackings-salaries-list-page'
            },
            {
              text: 'Paiement notes de frais',
              url: Paths.TRAVEL_EXPENSES_STAFF_SALARY_LIST_PAGE,
              itemKey: 'travel-expenses-staff-salary-list-page'
            },
            {
              text: 'Coordonnées bancaires',
              url: Paths.STAFF_BANK_DETAILS_LIST_PAGE,
              itemKey: 'staff-bank-details-list-page',
              activeUrls: [Paths.STAFF_UPDATE_PAGE]
            },
            {
              text: 'Saisie du PAS',
              url: Paths.ACCOUNTANT_PERSONAL_SPACE_AFFECTED_STAFFS_SALARIES_LIST_PAGE,
              itemKey: 'accountant-personal-space-affected-staffs-salaries-list-page',
              activeUrls: [Paths.ACCOUNTANT_PERSONAL_SPACE_AFFECTED_STAFFS_SALARIES_LIST_PAGE]
            },
            {
              text: 'Saisie du PAS (Honoraires AAT)',
              url: Paths.ACCOUNTANT_PERSONAL_SPACE_MISSION_TRACKINGS_SALARIES_LIST_PAGE,
              itemKey: 'accountant-personal-space-mission-trackings-salaries-list-page',
              activeUrls: [Paths.ACCOUNTANT_PERSONAL_SPACE_MISSION_TRACKINGS_SALARIES_LIST_PAGE]
            }
          ]
        },
        {
          text: 'Paramètres',
          itemKey: 'staff-parameters',
          items: [
            {
              text: 'Barèmes rémunération PCR',
              url: Paths.PCR_PAY_SCALES,
              itemKey: 'pcr-pay-scales-list-page',
              activeUrls: [Paths.PCR_PAY_SCALE_UPDATE_PAGE, Paths.PCR_PAY_SCALE_CREATE_PAGE]
            },
            {
              text: 'Barèmes rémunération encadrants',
              url: Paths.STAFF_PAY_SCALE_LIST_PAGE,
              itemKey: 'staff-pay-scale-list-page',
              activeUrls: [Paths.STAFF_PAY_SCALE_UPDATE_PAGE, Paths.STAFF_PAY_SCALE_CREATE_PAGE]
            },
            {
              text: 'Barèmes et types de missions AAT',
              url: Paths.MISSION_TYPE_LIST_PAGE,
              itemKey: 'mission-type-list-page',
              activeUrls: [Paths.MISSION_TYPE_UPDATE_PAGE, Paths.MISSION_TYPE_CREATE_PAGE]
            },
            {
              text: 'Textes des contrats',
              url: Paths.CONTRACT_TEMPLATE_LIST_PAGE,
              itemKey: 'contract-template-list-page',
              activeUrls: [
                Paths.CONTRACT_TEMPLATE_UPDATE_PAGE,
                Paths.CONTRACT_TEMPLATE_CREATE_PAGE
              ]
            },
            //!! client asked to remove this item
            // {
            //   text: 'Fiche de dispo - Formules souhaitées',
            //   url: Paths.SETTING_FORMULA_WISH_LIST,
            //   itemKey: 'setting-formula-wish-list'
            // },
            {
              text: 'Types de commentaires',
              url: Paths.SETTING_STAFF_COMMENT_SUBJECTS,
              itemKey: 'setting-staff-comment-subjects'
            }
          ]
        }
        // {
        //   text: 'Saisie du PAS',
        //   url: Paths.ACCOUNTANT_PERSONAL_SPACE_AFFECTED_STAFFS_SALARIES_LIST_PAGE,
        //   itemKey: 'accountant-personal-space-affected-staffs-salaries-list-page',
        //   activeUrls: [Paths.ACCOUNTANT_PERSONAL_SPACE_AFFECTED_STAFFS_SALARIES_LIST_PAGE]
        // },
        // {
        //   text: 'Saisie du PAS (Honoraires AAT)',
        //   url: Paths.ACCOUNTANT_PERSONAL_SPACE_MISSION_TRACKINGS_SALARIES_LIST_PAGE,
        //   itemKey: 'accountant-personal-space-mission-trackings-salaries-list-page',
        //   activeUrls: [Paths.ACCOUNTANT_PERSONAL_SPACE_MISSION_TRACKINGS_SALARIES_LIST_PAGE]
        // }
      ]
    },
    {
      itemKey: 'cse',
      text: 'CSE',
      hasAccess: [RoleEnum.ADMIN].includes(role),
      icon: BuildingIcon,
      items: [
        {
          itemKey: 'cse-list-page',
          text: 'Gestion des CSE',
          url: Paths.CSE_LIST_PAGE,
          activeUrls: [Paths.CSE_CREATE_PAGE, Paths.CSE_UPDATE_PAGE]
        }
      ]
    },

    {
      itemKey: 'supervisors',
      text: 'Encadrant',
      hasAccess: [RoleEnum.ACCOUNTANT].includes(role),
      icon: UserSevenIcon,
      items: [
        {
          text: 'Saisie du PAS',
          url: Paths.ACCOUNTANT_PERSONAL_SPACE_AFFECTED_STAFFS_SALARIES_LIST_PAGE,
          itemKey: 'accountant-personal-space-affected-staffs-salaries-list-page',
          activeUrls: [Paths.ACCOUNTANT_PERSONAL_SPACE_AFFECTED_STAFFS_SALARIES_LIST_PAGE]
        },
        {
          text: 'Saisie du PAS (Honoraires AAT)',
          url: Paths.ACCOUNTANT_PERSONAL_SPACE_MISSION_TRACKINGS_SALARIES_LIST_PAGE,
          itemKey: 'accountant-personal-space-mission-trackings-salaries-list-page',
          activeUrls: [Paths.ACCOUNTANT_PERSONAL_SPACE_MISSION_TRACKINGS_SALARIES_LIST_PAGE]
        }
      ]
    },
    {
      itemKey: 'stays',
      text: 'Composants des séjours',
      icon: FormIcon,
      hasAccess: [RoleEnum.ADMIN].includes(role),
      items: [
        {
          text: 'Pays/formules/options',
          itemKey: 'journey-basic-data',
          items: [
            {
              text: 'Pays',
              url: Paths.COUNTRY_LIST_PAGE,
              itemKey: 'country-list-page',
              activeUrls: [Paths.COUNTRY_CREATE_PAGE, Paths.COUNTRY_UPDATE_PAGE]
            },
            {
              text: 'Formules',
              url: Paths.FORMULA_WISH_LIST,
              itemKey: 'formula-wish-list-page',
              activeUrls: [
                Paths.FORMULA_WISH_LIST_CREATE_PAGE,
                Paths.FORMULA_WISH_LIST_UPDATE_PAGE
              ]
            },
            {
              text: 'Thèmes et options',
              url: Paths.JOURNEY_PLACE_OPTIONS_LIST_PAGE,
              itemKey: 'journey-place-options-list-page',
              activeUrls: [
                Paths.JOURNEY_PLACE_OPTION_UPDATE_PAGE,
                Paths.JOURNEY_PLACE_OPTION_CREATE_PAGE
              ]
            }
          ]
        },
        {
          text: 'Voyages',
          itemKey: 'journey-trips',
          items: [
            {
              text: 'Moyens de transport',
              url: Paths.SETTING_TRANSPORT_MEANS,
              itemKey: 'setting-transport-means'
            },
            {
              text: 'Trajets de voyages',
              url: Paths.TRIPS_LIST,
              itemKey: 'trips-list-page',
              activeUrls: [Paths.TRIP_UPDATE_PAGE, Paths.TRIP_CREATE_PAGE]
            },
            {
              text: 'Lieux de voyages',
              url: Paths.SETTING_TRIP_PLACES,
              itemKey: 'setting-trip-places'
            },
            {
              text: 'Regroupements voyages secondaires',
              url: Paths.TRIP_GROUPING_LIST_PAGE,
              itemKey: 'trip-grouping-list-page',
              activeUrls: [Paths.TRIP_GROUPING_CREATE_PAGE, Paths.TRIP_GROUPING_UPDATE_PAGE]
            }
          ]
        },
        {
          text: 'Lieux de séjour',
          url: Paths.JOURNEY_PLACE_LIST_PAGE,
          activeUrls: [Paths.JOURNEY_PLACE_UPDATE_PAGE, Paths.JOURNEY_PLACE_CREATE_PAGE],
          itemKey: 'journey-place-list-page'
        },
        {
          text: 'Produits et séjours',
          itemKey: 'journey-products',
          items: [
            {
              text: 'Produits',
              url: Paths.PRODUCTS_LIST_PAGE,
              itemKey: 'products-list-page',
              activeUrls: [Paths.PRODUCT_CREATE_PAGE, Paths.PRODUCT_UPDATE_PAGE]
            },
            {
              text: 'Tous les séjours',
              url: Paths.JOURNEY_LIST_PAGE,
              itemKey: 'journey-list-page',
              activeUrls: [Paths.PRODUCT_UPDATE_PAGE]
            }
          ]
        }
      ]
    },
    {
      itemKey: 'settings',
      text: 'Paramètres',
      icon: SettingIcon,
      hasAccess: [RoleEnum.ADMIN].includes(role),
      items: [
        {
          text: 'Général',
          itemKey: 'general-settings',
          items: [
            {
              text: 'Appréciations de fin de séjour',
              url: Paths.SURVEY_END_OF_STAY_TEMPLATES_LIST_PAGE,
              itemKey: 'end-of-stay-templates-list-page',
              activeUrls: [
                Paths.SURVEY_END_OF_STAY_TEMPLATE_CREATE_PAGE,
                Paths.SURVEY_END_OF_STAY_TEMPLATE_UPDATE_PAGE,
                Paths.SURVEY_END_OF_STAY_TEMPLATE_QUESTIONS_LIST_PAGE
              ]
            },
            {
              text: 'Modalités de paiement',
              itemKey: 'payment-conditions-list',
              url: Paths.PAYMENT_CONDITIONS_LIST_PAGE,
              activeUrls: [
                Paths.PAYMENT_CONDITIONS_UPDATE_PAGE,
                Paths.PAYMENT_CONDITIONS_CREATE_PAGE
              ]
            },
            {
              text: 'Assurances',
              url: Paths.INSURANCE_LIST_PAGE,
              itemKey: 'insurance-list-page',
              activeUrls: [Paths.INSURANCE_CREATE_PAGE, Paths.INSURANCE_UPDATE_PAGE]
            },
            { text: 'Périodes de séjours', url: Paths.SETTING_PERIODS, itemKey: 'setting-periods' }
          ]
        },
        {
          text: 'Site web',
          itemKey: 'site-web-settings',
          items: [
            {
              text: 'Codes réductions',
              url: Paths.PROMO_CODE_LIST_PAGE,
              itemKey: 'promo-code-list-page',
              activeUrls: [Paths.PROMO_CODE_CREATE_PAGE, Paths.PROMO_CODE_UPDATE_PAGE]
            },
            {
              text: 'Mots-clés AA',
              url: Paths.AA_KEYWORD_LIST_PAGE,
              itemKey: 'aa-keyword-list-page',
              activeUrls: [Paths.AA_KEYWORD_CREATE_PAGE, Paths.AA_KEYWORD_UPDATE_PAGE]
            },
            {
              text: 'Mots-clés santé',
              url: Paths.MEDICAL_TERM_LIST_PAGE,
              itemKey: 'medical-term-list-page',
              activeUrls: [Paths.MEDICAL_TERM_CREATE_PAGE, Paths.MEDICAL_TERM_UPDATE_PAGE]
            },
            {
              text: 'Réponses Comment avez-vous connu LEC ?',
              itemKey: 'setting-how-did-you-hear-about-us',
              url: Paths.SETTING_HOW_DID_YOU_HEAR_ABOUT_US
            },
            {
              text: 'Règles autorisation de sortie seul le soir',
              url: Paths.CAN_GO_OUT_ALONE_CONDITION_LIST_PAGE,
              itemKey: 'can-go-out-alone-condition-list-page',
              activeUrls: [
                Paths.CAN_GO_OUT_ALONE_CONDITION_CREATE_PAGE,
                Paths.CAN_GO_OUT_ALONE_CONDITION_UPDATE_PAGE
              ]
            }
          ]
        },
        {
          text: 'Espace client',
          itemKey: 'client-space-settings',
          items: [
            {
              text: 'Réponses question obtention brochure',
              url: Paths.HOW_DID_YOU_OBTAIN_LEC_BROCHURE_LIST_PAGE,
              itemKey: 'how-did-you-obtain-lec-brochure-list-page'
            },
            {
              text: 'Réponses question des loisirs',
              url: Paths.SETTING_HOBBIES,
              itemKey: 'setting-hobbies'
            },
            {
              text: 'Test de langue',
              url: Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE,
              itemKey: 'language-test-template-list-page',
              activeUrls: [
                Paths.LANGUAGE_TEST_TEMPLATE_DETAILS_PAGE,
                Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE
              ]
            },
            {
              text: 'Guide LEC',
              url: Paths.GUIDE_LIST_PAGE,
              itemKey: 'guide-list-page',
              activeUrls: [Paths.GUIDE_CREATE_PAGE, Paths.GUIDE_UPDATE_PAGE]
            },
            {
              text: "N° d'enregistrements Jeunesse et Sport",
              url: Paths.YOUTH_AND_SPORT_NUMBER_LIST_PAGE,
              itemKey: 'youth-and-sport-number-list-page',
              activeUrls: [
                Paths.YOUTH_AND_SPORT_NUMBER_CREATE_PAGE,
                Paths.YOUTH_AND_SPORT_NUMBER_UPDATE_PAGE
              ]
            },
            {
              text: 'Réponses question du caractère',
              url: Paths.CHARACTERISTICS_LIST_PAGE,
              itemKey: 'enrollment-characteristics-list-page'
            }
          ]
        }
      ]
    },
    {
      itemKey: 'personal-space',
      text: 'Mon espace personnel',
      icon: UserFourIcon,
      hasAccess: [RoleEnum.PCR].includes(role),
      items: [
        {
          text: 'Tableau de bord',
          itemKey: 'pcr-personal-space-dashboard',
          url: Paths.PCR_PERSONAL_SPACE_KANBAN
        },
        {
          text: 'Statistiques de base',
          itemKey: 'pcr-personal-space-basic-statistics',
          url: Paths.PCR_PERSONAL_SPACE_BASIC_STATISTICS
        },
        {
          text: 'Candidatures payées',
          itemKey: 'pcr-personal-space-paid-applications',
          url: Paths.PCR_PAYMENTS_PAGE
        },
        {
          text: 'Suivi des candidatures',
          itemKey: 'pcr-personal-space-follow-up',
          url: Paths.PCR_APPLICATIONS_PAGE
        }
      ]
    },
    {
      itemKey: 'ol-centers',
      text: 'My centres',
      hasAccess:
        [RoleEnum.Ol].includes(role) &&
        olType &&
        [OLTypeEnum.LEC, OLTypeEnum.RESIDENCE, OLTypeEnum.PARTNER].includes(olType),
      icon: BuildingThreeIcon,
      url: Paths.OL_PERSONAL_SPACE_CENTER_LIST_PAGE,
      activeUrls: [Paths.OL_PERSONAL_SPACE_CENTER_DETAILS_PAGE]
    },
    {
      itemKey: 'ol-oto-individual-places',
      text: t('oto.individual.title'),
      hasAccess: [RoleEnum.Ol].includes(role) && olType && [OLTypeEnum.PARTNER].includes(olType),
      icon: UserTwoIcon,
      url: Paths.OTO_INDIVIDUAL_PLACE_PERIODS_LIST_PAGE,
      activeUrls: [
        Paths.OTO_INDIVIDUAL_PLACE_PERIODS_LIST_PAGE,
        Paths.OTO_INDIVIDUAL_PLACES_BY_PERIOD_LIST_PAGE
      ]
    },
    {
      itemKey: 'ol-host-families',
      text: 'Host families',
      hasAccess:
        [RoleEnum.Ol].includes(role) &&
        olType &&
        [OLTypeEnum.LEC, OLTypeEnum.PARTNER].includes(olType),
      icon: FamilyIcon,
      url: Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_LIST_PAGE,
      activeUrls: [
        Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_CREATE_PAGE,
        Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_UPDATE_PAGE
      ]
    },
    {
      itemKey: 'ol-tefl',
      text: 'TEFL',
      hasAccess: [RoleEnum.Ol].includes(role) && olType && [OLTypeEnum.LEC].includes(olType),
      icon: TeachingIcon,
      url: Paths.OL_TEFL_LIST_PAGE,
      activeUrls: [Paths.OL_TEFL_CREATE_PAGE, Paths.OL_TEFL_UPDATE_PAGE]
    },
    {
      itemKey: 'ol-teaching-halls',
      text: 'Teaching hall',
      hasAccess:
        [RoleEnum.Ol].includes(role) &&
        olType &&
        [OLTypeEnum.LEC, OLTypeEnum.PARTNER].includes(olType),
      icon: BuildingTwoIcon,
      url: Paths.TEACHING_HALLS_LIST_PAGE,
      activeUrls: [Paths.TEACHING_HALL_CREATE_PAGE, Paths.TEACHING_HALL_UPDATE_PAGE]
    },
    {
      itemKey: 'ol-fees',
      text: 'Fees',
      hasAccess: [RoleEnum.Ol].includes(role) && olType && [OLTypeEnum.LEC].includes(olType),
      activeUrls: [
        `${Paths.OL_FEES_LIST_PAGE.replace('/*', '')}/${Paths.OL_FEES_COMMISSIONS_LIST_PAGE}`,
        `${Paths.OL_FEES_LIST_PAGE.replace('/*', '')}/${Paths.OL_DETAILS_HOST_FAMILY_PRICES_PAGE}`,
        `${Paths.OL_FEES_LIST_PAGE.replace('/*', '')}/${Paths.OL_FEES_TEFL_PRICE_LIST_PAGE}`
      ],
      icon: FeesThreeIcon,
      url: `${Paths.OL_FEES_LIST_PAGE.replace('/*', '')}/${Paths.OL_FEES_COMMISSIONS_LIST_PAGE}`
    },
    {
      itemKey: 'ol-activities',
      text: 'Activities',
      hasAccess: [RoleEnum.Ol].includes(role) && olType && [OLTypeEnum.LEC].includes(olType),
      icon: ActivityIcon,
      url: Paths.OL_PERSONAL_SPACE_ACTIVITY_LIST_PAGE,
      activeUrls: [
        Paths.OL_PERSONAL_SPACE_ACTIVITY_CREATE_PAGE,
        Paths.OL_PERSONAL_SPACE_ACTIVITY_UPDATE_PAGE
      ]
    },
    {
      itemKey: 'ol-mobile-numbers-for-french-leaders',
      text: 'Mobile phone numbers for French leader',
      hasAccess:
        [RoleEnum.Ol].includes(role) &&
        olType &&
        [OLTypeEnum.LEC, OLTypeEnum.PARTNER].includes(olType),
      icon: TelephoneIcon,
      url: Paths.OL_PERSONAL_SPACE_MOBILE_NUMBERS_FOR_FRENCH_LEADERS_LIST_PAGE
    },
    {
      itemKey: 'ol-coach-companies',
      text: 'Coach companies',
      hasAccess:
        [RoleEnum.Ol].includes(role) &&
        olType &&
        [OLTypeEnum.LEC, OLTypeEnum.RESIDENCE, OLTypeEnum.PARTNER].includes(olType),
      icon: BusIcon,
      url: Paths.OL_PERSONAL_SPACE_COACH_COMPANIES_LIST_PAGE,
      activeUrls: [
        Paths.OL_PERSONAL_SPACE_COACH_COMPANIES_CREATE_PAGE,
        Paths.OL_PERSONAL_SPACE_COACH_COMPANIES_UPDATE_PAGE
      ]
    },
    {
      itemKey: 'client-personal-space-home',
      text: 'Home',
      hasAccess: [RoleEnum.CLIENT].includes(role),
      icon: HomeIcon,
      url: constructClientPath(Paths.CLIENT_PERSONAL_SPACE_HOME, selectedEnrollmentId)
    },
    {
      itemKey: 'client-personal-space-reservations',
      text: 'Ma réservation',
      hasAccess: [RoleEnum.CLIENT].includes(role),
      icon: CalendarSevenIcon,
      isIconFilled: true,
      items: [
        {
          text: 'Contrat, facture, attestations',
          url: constructClientPath(Paths.CLIENT_PERSONAL_SPACE_PAYMENT, selectedEnrollmentId),
          itemKey: 'client-personal-space-reservations-payment'
        },
        {
          text: 'Régler le solde du séjour',
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_FINALIZE_PAYMENT,
            selectedEnrollmentId
          ),
          hasAccess: cseBillingType !== BillingTypeEnum.FCSE && !enrollmentCancelled,
          itemKey: 'client-personal-space-reservations-finalize-payment'
        },
        {
          hasAccess: !enrollmentCancelled,
          text: 'Annuler le séjour',
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_CANCEL_JOURNEY,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-reservations-cancel-journey' as SidebarItemKey
        }
      ]
    },
    {
      itemKey: 'client-personal-space-information',
      text: 'Formalités et infos à compléter avant départ',
      hasAccess: [RoleEnum.CLIENT].includes(role) && !enrollmentCancelled,
      icon: SheetOutlineIcon,
      items: [
        {
          text: 'Démarches administratives obligatoires',
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_ADMINSTRATION_PROCEDURES,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-administration-procedures'
        },
        {
          text: "Papiers d'identité de l'enfant",
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_INFORMATION_IDENTITY,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-identity'
        },
        ...(enrollmentHasTest
          ? [
              {
                text: 'Test de niveau',
                url: constructClientPath(
                  Paths.CLIENT_PERSONAL_SPACE_INFORMATION_TEST_LEVEL,
                  selectedEnrollmentId
                ),
                itemKey: 'client-personal-space-information-test-level' as SidebarItemKey
              }
            ]
          : []),
        {
          text: 'Profil de mon enfant - Dossier complémentaire',
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_INFORMATION_CHILD_PROFILE,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-child-profile'
        },
        {
          text: 'Fiche sanitaire de liaison',
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_INFORMATION_MEDICAL_STATEMENT,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-medical-statement'
        },
        {
          text: 'Qui vient chercher votre enfant à la fin du séjour ?',
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_INFORMATION_PICKUP,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-pickup'
        },
        {
          text: 'Mes infos de trajet individuel',
          // only a One to One Individuel family books its own aller/retour
          hasAccess: !!enrollmentIsOtoIndividual,
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_INFORMATION_INDIVIDUAL_TRIP,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-individual-trip'
        }
      ]
    },
    {
      itemKey: 'client-personal-space-trip-information',
      text: 'Mes infos pratiques sur le séjour',
      hasAccess: [RoleEnum.CLIENT].includes(role) && !enrollmentCancelled,
      icon: ListApprovedIcon,
      items: [
        {
          text: 'Guide LEC',
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_TRIP_INFORMATION_LEC_GUIDE,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-lec-guide'
        },
        {
          text: 'La to-do liste pour bien préparer le séjour de votre enfant',
          // the ECL pages behind it describe a group journey, not a One to One Individuel stay
          hasAccess: !enrollmentIsOtoIndividual,
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_JOURNEY_PREPARATION,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-journey-preparation'
        },
        {
          text: 'Check-list des documents à présenter le jour du départ',
          // the ECL pages behind it describe a group journey, not a One to One Individuel stay
          hasAccess: !enrollmentIsOtoIndividual,
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_DEPARTURE_CHECKLIST,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-departure-checklist'
        },
        {
          text: 'RDV trajet aller/retour',
          // an OTO Individuel participant organises their own travel: there is no trip to meet
          hasAccess: !enrollmentIsOtoIndividual,
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_INFORMATION_TRIPS,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-trips'
        },
        ...(enrollmentContactVersion
          ? [
              {
                text:
                  enrollmentContactVersion === ContactVersionEnum.CENTER_WITH_TRANSPORT ||
                  enrollmentContactVersion === ContactVersionEnum.HOST_FAMILY_WITH_TRANSPORT
                    ? 'Coordonnées pendant le séjour'
                    : 'RDV lieu de séjour',
                // the page is built from the enrollment center, which an OTO Individuel
                // enrollment never has: it gets its own contact page below
                hasAccess: !enrollmentIsOtoIndividual,
                url: constructClientPath(
                  Paths.CLIENT_PERSONAL_SPACE_TRIP_INFORMATION_CONTACT,
                  selectedEnrollmentId
                ),
                itemKey: 'client-personal-space-information-trip-contact' as SidebarItemKey
              }
            ]
          : []),
        {
          text: 'Coordonnées pendant le séjour',
          hasAccess: !!enrollmentIsOtoIndividual,
          url: constructClientPath(
            Paths.CLIENT_PERSONAL_SPACE_TRIP_INFORMATION_OTO_CONTACT,
            selectedEnrollmentId
          ),
          itemKey: 'client-personal-space-information-oto-contact'
        }
      ]
    }

    // UI Components
    // {
    //   hasAccess: [RoleEnum.ADMIN].includes(role),
    //   itemKey: 'ui',
    //   text: 'UI',
    //   icon: SettingIcon,
    //   items: [
    //     {
    //       text: 'Inputs',
    //       itemKey: 'ui-inputs',
    //       url: Paths.UI_INPUTS_PAGE
    //     }
    //   ]
    // }
  ]

  return allItems.filter((item) => {
    if (!hideItems) return true

    // Exclude the top-level item if it's in the hide list
    if (hideItems.includes(item.itemKey)) return false

    if (item.hasAccess === false) return false

    // If it has subitems, check if any subitems are valid
    if (item.items) {
      const filterSubItems = (items: SidebarItemType[]): SidebarItemType[] => {
        return items
          .map((subItem) => {
            if (hideItems.includes(subItem.itemKey)) return null
            if (subItem.items) {
              const filtered = filterSubItems(subItem.items)
              return filtered.length > 0 ? { ...subItem, items: filtered } : null
            }
            return subItem
          })
          .filter(Boolean) as SidebarItemType[]
      }

      item.items = filterSubItems(item.items)
      return item.items.length > 0
    }

    return true
  })
}
